import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { OrderByFieldApproveEmployees, SearchApprovesContextValue, SearchFilters } from './types'
import { useQuery } from 'react-query'
import { getCompetencies } from 'search/api/requests'
import { constructSearchBody, parseFilters, stringifyFiltersApproveEmployees } from './converters'
import { useSelector } from 'react-redux'
import { authInfo } from 'store/selectors/auth-selectors'
import { Roles } from 'utils/enums/different'
import { EmployeeApproveInfo } from 'search/context/types'
import { fetchApproveRequests } from '../api/requests'

const SearchApprovesContext = createContext<SearchApprovesContextValue | null>(null)

interface Props {
    children: React.ReactNode
}

export const SearchApprovesContextProvider: FC<Props> = ({ children }) => {
    const auth = useSelector(authInfo)
    const isUserIsExternalConsultant = auth.user?.role === Roles.externalConsultant

    const { data: fetchedCompetencies } = useQuery('fetchCompetencies', () => getCompetencies(), {
        enabled: !isUserIsExternalConsultant,
        refetchOnWindowFocus: false,
        select: (data) => data.sort(),
    })

    const [searchParams, setSearchParams] = useSearchParams()

    // restore filters from URL
    const parsedParams = useMemo(() => parseFilters(searchParams), [searchParams])
    const [competencies, setCompetencies] = useState<string[]>(parsedParams.competencies)
    const [searchString, setSearchString] = useState<string>(parsedParams.searchString)
    const [newLevels, setNewLevels] = useState<number[]>(parsedParams.newLevels)

    const [orderBy, setOrderBy] = useState<OrderByFieldApproveEmployees | null>(parsedParams.orderBy)

    const [offset, setOffset] = useState<number>(0)
    const [total, setTotal] = useState<number>(0)
    const [loading, setLoading] = useState<boolean>(false)
    const [results, setResults] = useState<EmployeeApproveInfo[]>([])

    const hasMounted = useRef(false)

    useEffect(() => {
        hasMounted.current = true

        return () => {
            hasMounted.current = false
        }
    }, [])

    const updateRequestsList = useCallback(() => {

        async function getApproveRequests(params: SearchFilters) {
            if (!hasMounted.current) return
            setLoading(true)

            const result = await fetchApproveRequests(constructSearchBody(params))

            if (!hasMounted.current) return

            setLoading(false)
            if (result?.data) {
                setResults(result?.data)
                setTotal(result?.total)
                setOffset(20)
            }
        }

        if (!isUserIsExternalConsultant) {

            setOffset(0)
            setTotal(0)

            setSearchParams(
              stringifyFiltersApproveEmployees({
                  newLevels,
                  competencies,
                  searchString,
                  orderBy,
              })
            )
            getApproveRequests({
                searchString,
                newLevels,
                competencies,
                orderBy,
                offset: 0,
            })
        }
    },[newLevels, orderBy, competencies, searchString, isUserIsExternalConsultant]);

    useEffect(() => {
        updateRequestsList();
    }, [newLevels, orderBy, competencies, searchString, isUserIsExternalConsultant])

    useEffect(() => {
        setNewLevels(parsedParams.newLevels)
        setSearchString(parsedParams.searchString)
        setCompetencies(parsedParams.competencies)
        setOrderBy(parsedParams.orderBy)
    }, [])

    const value = useMemo(
        (): SearchApprovesContextValue => ({
            loading,
            total,
            setTotal,
            offset,
            setOffset,
            searchString,
            setSearchString,
            results,
            setResults,
            newLevels,
            setNewLevels,
            competencies,
            setCompetencies,
            orderBy,
            setOrderBy,
            fetchedCompetencies,
            updateRequestsList,
        }),
        [
            loading,
            offset,
            total,
            setTotal,
            setOffset,
            newLevels,
            setNewLevels,
            searchString,
            results,
            setResults,
            orderBy,
            setOrderBy,
            setSearchString,
            fetchedCompetencies,
            competencies,
            setCompetencies,
            updateRequestsList,
        ]
    )

    return <SearchApprovesContext.Provider value={value}>{children}</SearchApprovesContext.Provider>
}

export const useSearchApprovedContext = (): SearchApprovesContextValue => {
    const value = useContext(SearchApprovesContext)
    if (!value) {
        throw new Error('useSearchApprovedContext should be used within SearchApprovesContextProvider')
    }
    return value
}
